import type { Issue } from "contracts/api/issue/responses";
import {
  MAP_CHANGE_TYPE,
  MAP_EVIDENCE_KIND,
  MAP_EVIDENCE_TOPIC,
} from "../../enums/map";
import { coverageEvidence, issueEvidence } from "./mapEvidence";
import {
  canonicalIssueKey,
  categoryForMapChange,
  evidenceStateForChange,
  futureBriefLevelForChange,
  highestSeverity,
  migrationWave,
  uniqueCanonicalIssues,
} from "./mapPreviewActionRules";
import type { MapPreviewAction } from "./mapPreviewActions";
import type { MapChange, MapPageFact } from "./mapTypes";

export function consolidateMapPreviewActions(
  actions: MapPreviewAction[],
): MapPreviewAction[] {
  const families = new Map<string, MapPreviewAction[]>();
  for (const action of actions) {
    const key = actionFamilyKey(action);
    families.set(key, [...(families.get(key) ?? []), action]);
  }
  return [...families.values()].map(mergeActionFamily);
}

function mergeActionFamily(rows: MapPreviewAction[]): MapPreviewAction {
  const representative = rows[0] as MapPreviewAction;
  if (rows.length === 1) return representative;
  const causalIssues = uniqueCanonicalIssues(
    rows.flatMap((row) => row.causalIssues),
  );
  const change = mergedChange(rows, causalIssues);
  const category = categoryForMapChange(change);
  const evidenceSeverity = highestSeverity(causalIssues);
  const evidenceState = evidenceStateForChange(change, causalIssues);
  return {
    change,
    sourceChangeIds: [...new Set(rows.flatMap((row) => row.sourceChangeIds))],
    category,
    futureBriefLevel: futureBriefLevelForChange(change),
    evidenceState,
    evidenceSeverity,
    causalIssues,
    relatedFlowLabels: [
      ...new Set(rows.flatMap((row) => row.relatedFlowLabels)),
    ],
    concernCount: causalIssues.length,
    wave: migrationWave(change, category, evidenceState, evidenceSeverity),
  };
}

function mergedChange(
  rows: MapPreviewAction[],
  causalIssues: Issue[],
): MapChange {
  const representative = rows[0] as MapPreviewAction;
  const changes = rows.map((row) => row.change);
  const pageFacts = uniquePageFacts(
    changes.flatMap((change) => change.pageFacts),
  );
  const pages = pageFacts.map((fact) => fact.page);
  const currentGroups = [
    ...new Set(changes.map((change) => change.currentGroupLabel)),
  ];
  const structuralEvidence = [
    ...new Map(
      changes
        .flatMap((change) => change.evidence)
        .filter((evidence) => evidence.kind !== MAP_EVIDENCE_KIND.ISSUE)
        .filter((evidence) => evidence.kind !== MAP_EVIDENCE_KIND.COVERAGE)
        .map((evidence) => [
          `${evidence.kind}:${evidence.topic}:${evidence.detail}`,
          evidence,
        ]),
    ).values(),
  ];
  const pageIds = new Set(pages.map((page) => page.id));
  const targetRole = mergedTargetRole(changes);
  const title = mergedTitle(representative.change, targetRole);
  return {
    ...representative.change,
    title,
    targetRole,
    summary: `${pages.length} pages share one supported architecture decision and are combined in this draft action.`,
    currentState: `${pages.length} pages across ${currentGroups.length} current content areas share the same supported structural concern.`,
    currentGroupLabel: `${currentGroups.length} current content areas`,
    targetGroup:
      representative.change.type === MAP_CHANGE_TYPE.IMPROVE_PATTERN
        ? title
        : representative.change.targetGroup,
    pages,
    pageFacts,
    issues: causalIssues,
    evidence: [
      ...(representative.change.type === MAP_CHANGE_TYPE.IMPROVE_PATTERN
        ? [
            {
              kind: "structure" as const,
              topic: MAP_EVIDENCE_TOPIC.SHARED_PAGE_PATTERN,
              detail: `${pages.length} pages share the ${targetRole} role across ${currentGroups.length} current content areas.`,
            },
          ]
        : structuralEvidence),
      ...issueEvidence(causalIssues, pageIds),
      coverageEvidence(pageFacts),
    ],
    uncertainties: [
      ...new Set(changes.flatMap((change) => change.uncertainties)),
    ],
    relatedPageIds: [
      ...new Set(changes.flatMap((change) => change.relatedPageIds)),
    ],
  };
}

function actionFamilyKey(action: MapPreviewAction): string {
  const change = action.change;
  const issueKeys = action.causalIssues.map(canonicalIssueKey).sort().join("+");
  if (change.type === MAP_CHANGE_TYPE.CLARIFY_DESTINATION) {
    const relationship =
      change.evidence.find(
        (evidence) => evidence.topic === MAP_EVIDENCE_TOPIC.ONWARD_RELATIONSHIP,
      )?.detail ?? change.targetRole;
    return `${change.type}:${relationship}:${issueKeys || "structural"}`;
  }
  if (change.type === MAP_CHANGE_TYPE.IMPROVE_PATTERN && issueKeys) {
    const criteria = [
      ...new Set(
        action.causalIssues.map(
          (issue) => issue.primarySubcriterionKey ?? issue.metricKey,
        ),
      ),
    ]
      .sort()
      .join("+");
    return `${change.type}:${criteria}`;
  }
  return `${change.type}:${change.currentGroupKey}:${issueKeys || change.targetRole}`;
}

function mergedTitle(
  change: MapChange,
  targetRole = change.targetRole,
): string {
  if (change.adjusted) return change.title;
  if (change.type === MAP_CHANGE_TYPE.CLARIFY_DESTINATION) {
    return "Clarify the shared onward destination";
  }
  if (change.type === MAP_CHANGE_TYPE.IMPROVE_PATTERN) {
    return `Improve the shared ${targetRole.toLowerCase()}`;
  }
  return change.title;
}

function mergedTargetRole(changes: MapChange[]): string {
  const roles = [...new Set(changes.map((change) => change.targetRole))];
  return roles.length === 1 ? (roles[0] as string) : "content pattern";
}

function uniquePageFacts(facts: MapPageFact[]): MapPageFact[] {
  return [...new Map(facts.map((fact) => [fact.page.id, fact])).values()];
}
